/**
 * Retrieval & Hybrid Search Dashboard
 * Shows metrics, A/B testing, BM25 index status, and performance stats
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';
import {
  Search,
  Zap,
  TrendingUp,
  Activity,
  Database,
  Cpu,
  CircuitBoard,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  BarChart3,
  Layers,
  Target,
} from 'lucide-react';
import {
  getRetrievalMetrics,
  getBM25IndexStatus,
  buildBM25Index,
  getABTestStats,
  type RetrievalMetrics,
  type BM25IndexStatus,
  type ABTestStats,
} from '../../api/retrieval';
import { useWorkspace } from '../../hooks/useWorkspace';

export function RetrievalDashboard() {
  const { projectId, tenantId } = useWorkspace();
  const queryClient = useQueryClient();
  const [days, setDays] = useState(7);

  // Fetch retrieval metrics
  const { data: metrics, isLoading: loadingMetrics } = useQuery({
    queryKey: ['retrieval-metrics', projectId, days],
    queryFn: () => getRetrievalMetrics(projectId || undefined, days),
    enabled: !!projectId,
    refetchInterval: 30000, // Refresh every 30s
  });

  // Fetch BM25 index status
  const { data: bm25Status, isLoading: loadingBM25 } = useQuery({
    queryKey: ['bm25-index-status', projectId],
    queryFn: () => getBM25IndexStatus(projectId!),
    enabled: !!projectId,
    refetchInterval: 10000, // Refresh every 10s
  });

  // Fetch A/B test stats
  const { data: abStats, isLoading: loadingAB } = useQuery({
    queryKey: ['ab-test-stats', projectId, days],
    queryFn: () => getABTestStats(projectId || undefined, days),
    enabled: !!projectId,
    refetchInterval: 60000, // Refresh every minute
  });

  // Build BM25 index mutation
  const buildIndexMutation = useMutation({
    mutationFn: () => buildBM25Index(projectId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bm25-index-status'] });
      alert('BM25 index build job started. Check job status for progress.');
    },
    onError: (error: any) => {
      alert(`Failed to start BM25 index build: ${error.message}`);
    },
  });

  if (!projectId) {
    return (
      <Card title="Retrieval & Hybrid Search">
        <div className="text-center py-8 text-gray-500">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p>Please select a project to view retrieval metrics</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Retrieval & Hybrid Search
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Monitor hybrid search performance, BM25 indexing, and A/B testing
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>

      {/* BM25 Index Status */}
      <Card title="BM25 Index Status">
        {loadingBM25 ? (
          <div className="text-center py-8 text-gray-500">Loading index status...</div>
        ) : bm25Status ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {bm25Status.index_exists ? (
                  <CheckCircle className="h-6 w-6 text-green-600" />
                ) : (
                  <AlertCircle className="h-6 w-6 text-orange-600" />
                )}
                <div>
                  <div className="font-semibold text-gray-900 dark:text-gray-100">
                    {bm25Status.index_exists ? 'Index Ready' : 'Index Not Built'}
                  </div>
                  <div className="text-sm text-gray-500">
                    {bm25Status.total_documents} documents indexed
                  </div>
                </div>
              </div>
              {!bm25Status.index_exists && (
                <Button
                  size="sm"
                  onClick={() => {
                    if (window.confirm('Build BM25 index for this project? This may take a few minutes.')) {
                      buildIndexMutation.mutate();
                    }
                  }}
                  disabled={buildIndexMutation.isPending || bm25Status.build_status === 'building'}
                >
                  {buildIndexMutation.isPending || bm25Status.build_status === 'building' ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Building...
                    </>
                  ) : (
                    <>
                      <Database className="h-4 w-4" />
                      Build Index
                    </>
                  )}
                </Button>
              )}
            </div>

            {bm25Status.index_exists && (
              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Index Size</div>
                  <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    {bm25Status.index_size_mb.toFixed(2)} MB
                  </div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Documents</div>
                  <div className="text-lg font-bold text-green-600 dark:text-green-400">
                    {bm25Status.total_documents.toLocaleString()}
                  </div>
                </div>
                {bm25Status.last_built_at && (
                  <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Last Built</div>
                    <div className="text-sm font-medium text-purple-600 dark:text-purple-400">
                      {new Date(bm25Status.last_built_at).toLocaleDateString()}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">Failed to load index status</div>
        )}
      </Card>

      {/* Retrieval Metrics */}
      <Card title="Retrieval Performance">
        {loadingMetrics ? (
          <div className="text-center py-8 text-gray-500">Loading metrics...</div>
        ) : metrics ? (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Search className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  <div className="text-xs text-gray-500">Total Queries</div>
                </div>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {metrics.total_queries.toLocaleString()}
                </div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <div className="text-xs text-gray-500">Avg Latency</div>
                </div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {metrics.avg_latency_ms.toFixed(0)}ms
                </div>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Layers className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  <div className="text-xs text-gray-500">Vector Results</div>
                </div>
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {metrics.avg_vector_count.toFixed(1)}
                </div>
              </div>
              <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                  <div className="text-xs text-gray-500">BM25 Results</div>
                </div>
                <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {metrics.avg_bm25_count.toFixed(1)}
                </div>
              </div>
            </div>

            {/* Reranking Usage */}
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    Cross-Encoder Reranking
                  </div>
                </div>
                <Badge variant={metrics.rerank_usage_percent > 50 ? 'success' : 'default'}>
                  {metrics.rerank_usage_percent.toFixed(1)}% usage
                </Badge>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
                <div
                  className="bg-yellow-600 dark:bg-yellow-400 h-2 rounded-full transition-all"
                  style={{ width: `${metrics.rerank_usage_percent}%` }}
                />
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Reranking is automatically enabled for complex/compliance queries
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">Failed to load metrics</div>
        )}
      </Card>

      {/* A/B Testing Stats */}
      {abStats && (abStats.variant_a_count > 0 || abStats.variant_b_count > 0) && (
        <Card title="A/B Testing Comparison">
          {loadingAB ? (
            <div className="text-center py-8 text-gray-500">Loading A/B test stats...</div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="border-2 border-blue-200 dark:border-blue-800 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="default" className="bg-blue-600 text-white">
                      Variant A
                    </Badge>
                    <span className="text-xs text-gray-500">Hybrid Search</span>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm">
                      <span className="text-gray-500">Queries:</span>{' '}
                      <span className="font-semibold">{abStats.variant_a_count}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-500">Avg Latency:</span>{' '}
                      <span className="font-semibold">
                        {abStats.variant_a_avg_latency_ms.toFixed(0)}ms
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-500">Avg Results:</span>{' '}
                      <span className="font-semibold">
                        {abStats.variant_a_avg_results.toFixed(1)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="border-2 border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="default" className="bg-gray-600 text-white">
                      Variant B
                    </Badge>
                    <span className="text-xs text-gray-500">Vector-Only (Control)</span>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm">
                      <span className="text-gray-500">Queries:</span>{' '}
                      <span className="font-semibold">{abStats.variant_b_count}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-500">Avg Latency:</span>{' '}
                      <span className="font-semibold">
                        {abStats.variant_b_avg_latency_ms.toFixed(0)}ms
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-500">Avg Results:</span>{' '}
                      <span className="font-semibold">
                        {abStats.variant_b_avg_results.toFixed(1)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Performance Comparison */}
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Performance Comparison
                </div>
                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Latency Difference:</span>
                    <span
                      className={`font-semibold ${
                        abStats.variant_a_avg_latency_ms < abStats.variant_b_avg_latency_ms
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}
                    >
                      {(
                        ((abStats.variant_a_avg_latency_ms - abStats.variant_b_avg_latency_ms) /
                          abStats.variant_b_avg_latency_ms) *
                        100
                      ).toFixed(1)}
                      %
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Results Difference:</span>
                    <span
                      className={`font-semibold ${
                        abStats.variant_a_avg_results > abStats.variant_b_avg_results
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}
                    >
                      {(
                        ((abStats.variant_a_avg_results - abStats.variant_b_avg_results) /
                          abStats.variant_b_avg_results) *
                        100
                      ).toFixed(1)}
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* System Info */}
      <Card title="System Configuration">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Cpu className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                <div className="font-medium text-gray-900 dark:text-gray-100">CPU Mode</div>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Cross-encoder runs on CPU by default. No setup required.
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <CircuitBoard className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                <div className="font-medium text-gray-900 dark:text-gray-100">GPU Mode</div>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Automatic if CUDA is available. Requires PyTorch with CUDA support.
              </div>
            </div>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-blue-900 dark:text-blue-100 mb-1">
                  No Account Setup Required
                </div>
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  The hybrid search system works out of the box:
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>CPU mode: Automatic fallback, no configuration needed</li>
                    <li>GPU mode: Auto-detects CUDA if available (requires PyTorch with CUDA)</li>
                    <li>Azure AI Service: Optional, requires Azure OpenAI credentials (graceful degradation if not configured)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

